<script>
import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';
import { SearchOutlined } from '@ant-design/icons-vue';
import columns, { STATUSES, getStatusLabel } from './columns';

const b = bem('orders-list');

export default {
  components: {
    SearchOutlined,
  },
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      showEdit: false,
      selectedId: null,
      limit: 10,
      offset: 0,
      filteredInfo: {},
      sortedInfo: {},
      pmUserSearchText: '',
      pmUserSearchResults: [],
      selectedPmUser: null,
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getOrdersAction: 'orders/getOrders',
      updateOrderAction: 'orders/updateOrder',
      deleteOrderAction: 'orders/deleteOrder',
      searchUsersAction: 'users/searchUsers',
    }),
    getOrders() {
      const filters = {};
      if (this.filteredInfo.status && this.filteredInfo.status.length > 0) {
        [filters.status] = this.filteredInfo.status;
      }
      if (this.filteredInfo.pmUser && this.filteredInfo.pmUser.length > 0) {
        [filters.pmUserId] = this.filteredInfo.pmUser;
      }

      const sorting = {};
      if (this.sortedInfo.columnKey) {
        sorting.field = this.sortedInfo.columnKey;
        sorting.order = this.sortedInfo.order === 'ascend' ? 'asc' : 'desc';
      }

      this.getOrdersAction({
        limit: this.limit,
        offset: this.offset,
        companyId: this.companyId,
        filters,
        sorting,
      });
    },
    setPagination(page, pageSize) {
      this.offset = (page - 1) * pageSize;
      this.limit = pageSize;
    },
    handleTableChange(pagination, filters, sorter) {
      this.filteredInfo = filters;
      this.sortedInfo = sorter;
      this.setPagination(pagination.current, pagination.pageSize);
      this.getOrders();
    },
    removeFilter(key) {
      const newFilteredInfo = { ...this.filteredInfo };
      newFilteredInfo[key] = null;
      this.filteredInfo = newFilteredInfo;
      this.getOrders();
    },
    clearAllFilters() {
      this.filteredInfo = {};
      this.selectedPmUser = null;
      this.pmUserSearchText = '';
      this.getOrders();
    },
    async searchPmUsers(searchText) {
      if (!searchText || searchText.length < 2) {
        this.pmUserSearchResults = [];
        return;
      }
      this.pmUserSearchText = searchText;
      try {
        await this.searchUsersAction({
          searchField: searchText,
          limit: 100,
          offset: 0,
        });
        this.pmUserSearchResults = this.$store.state.users.users || [];
      } catch (error) {
        this.pmUserSearchResults = [];
      }
    },
    selectPmUser(user) {
      this.selectedPmUser = user;
      this.pmUserSearchText = `${user.firstName} ${user.lastName}`;
    },
    applyPmUserFilter() {
      if (this.selectedPmUser) {
        this.filteredInfo = {
          ...this.filteredInfo,
          pmUser: [this.selectedPmUser.id],
        };
        this.getOrders();
      }
    },
    clearPmUserFilter() {
      this.selectedPmUser = null;
      this.pmUserSearchText = '';
      this.pmUserSearchResults = [];
      const newFilteredInfo = { ...this.filteredInfo };
      delete newFilteredInfo.pmUser;
      this.filteredInfo = newFilteredInfo;
    },
    getFilterLabel(key, value) {
      const column = this.tableColumns.find((col) => col.key === key);
      const title = column ? column.title : key;

      if (key === 'status' && Array.isArray(value) && value.length > 0) {
        return `${title}: ${value.map((v) => getStatusLabel(v)).join(', ')}`;
      }

      if (key === 'pmUser' && this.selectedPmUser) {
        return `${title}: ${this.selectedPmUser.firstName} ${this.selectedPmUser.lastName}`;
      }

      if (Array.isArray(value)) {
        return `${title}: ${value.join(', ')}`;
      }

      return `${title}: ${value}`;
    },
    openEditWindow(id) {
      this.selectedId = id;
      this.showEdit = true;
    },
    updateOrder(order) {
      const filters = {};
      if (this.filteredInfo.status && this.filteredInfo.status.length > 0) {
        [filters.status] = this.filteredInfo.status;
      }

      const sorting = {};
      if (this.sortedInfo.columnKey) {
        sorting.field = this.sortedInfo.columnKey;
        sorting.order = this.sortedInfo.order === 'ascend' ? 'asc' : 'desc';
      }

      this.updateOrderAction({
        order: {
          id: order.id,
          status: order.status,
          companyId: order.companyId || this.companyId || null,
        },
        limit: this.limit,
        offset: this.offset,
        companyId: this.companyId,
        filters,
        sorting,
      });
    },
    deleteOrder(id) {
      const filters = {};
      if (this.filteredInfo.status && this.filteredInfo.status.length > 0) {
        [filters.status] = this.filteredInfo.status;
      }

      const sorting = {};
      if (this.sortedInfo.columnKey) {
        sorting.field = this.sortedInfo.columnKey;
        sorting.order = this.sortedInfo.order === 'ascend' ? 'asc' : 'desc';
      }

      this.deleteOrderAction({
        id,
        limit: this.limit,
        offset: this.offset,
        companyId: this.companyId,
        filters,
        sorting,
      });
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    formatPrice(price) {
      if (!price && price !== 0) return '-';
      return `$${Number(price).toFixed(2)}`;
    },
    formatUserName(user) {
      if (!user) return 'website';
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'website';
    },
    formatPayment(payments) {
      if (!payments || payments.length === 0) return '-';
      const payment = payments[0];
      return `${payment.paymentMethod || 'N/A'}: $${Number(payment.amount || 0).toFixed(2)}`;
    },
  },
  mounted() {
    this.getOrders();
  },
  computed: {
    ...mapState({
      orders: (state) => state.orders.orders,
      total: (state) => state.orders.total,
    }),
    statusFilters() {
      return STATUSES.map((s) => ({
        text: s.label,
        value: s.value,
      }));
    },
    tableColumns() {
      return columns(this.filteredInfo, this.statusFilters);
    },
    activeFilters() {
      return Object.keys(this.filteredInfo).filter((key) => {
        const value = this.filteredInfo[key];
        return value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : value !== '');
      });
    },
  },
  watch: {
    companyId() {
      this.offset = 0;
      this.filteredInfo = {};
      this.sortedInfo = {};
      this.getOrders();
    },
  },
};
</script>

<template>
  <div :class="b()">
    <div
      v-if="activeFilters.length > 0"
      :class="b('active-filters')"
      style="margin-bottom: 16px; padding: 16px; background: #f5f5f5; border-radius: 4px;"
    >
      <div style="margin-bottom: 8px; font-weight: bold;">Active filters:</div>
      <a-tag
        v-for="key in activeFilters"
        :key="key"
        closable
        @close="removeFilter(key)"
        color="blue"
        style="margin-bottom: 8px;"
      >
        {{ getFilterLabel(key, filteredInfo[key]) }}
      </a-tag>
      <a-button type="link" size="small" @click="clearAllFilters">Clear all</a-button>
    </div>

    <div :class="b('table')">
      <a-table
        :columns="tableColumns"
        :dataSource="orders"
        :pagination="{
          pageSize: limit,
          total: total,
          current: (offset / limit) + 1,
          onChange: setPagination,
        }"
        :rowKey="(record) => record.id"
        @change="handleTableChange"
      >
        <template
          #customFilterDropdown="{
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            column,
          }"
        >
          <div style="padding: 8px">
            <template v-if="column.key === 'pmUser'">
              <a-input
                ref="pmUserSearchInput"
                placeholder="Search by name"
                :value="pmUserSearchText"
                style="width: 250px; margin-bottom: 8px; display: block"
                @change="(e) => searchPmUsers(e.target.value)"
              />
              <div
                v-if="pmUserSearchResults.length > 0"
                style="
                  max-height: 200px;
                  overflow-y: auto;
                  margin-bottom: 8px;
                  border: 1px solid #d9d9d9;
                  border-radius: 2px;
                "
              >
                <div
                  v-for="user in pmUserSearchResults"
                  :key="user.id"
                  tabindex="0"
                  role="button"
                  style="padding: 8px; cursor: pointer; border-bottom: 1px solid #f0f0f0;"
                  :style="{ background: selectedPmUser?.id === user.id ? '#e6f7ff' : 'white' }"
                  @click="selectPmUser(user)"
                  @keypress.enter="selectPmUser(user)"
                  @mouseenter="$event.target.style.background = '#f5f5f5'"
                  @mouseleave="
                    $event.target.style.background = selectedPmUser?.id === user.id
                      ? '#e6f7ff'
                      : 'white'
                  "
                  @focus="$event.target.style.background = '#f5f5f5'"
                  @blur="
                    $event.target.style.background = selectedPmUser?.id === user.id
                      ? '#e6f7ff'
                      : 'white'
                  "
                >
                  {{ user.firstName }} {{ user.lastName }}
                </div>
              </div>
              <div
                v-else-if="pmUserSearchText && pmUserSearchText.length >= 2"
                style="padding: 8px; color: #999; text-align: center; margin-bottom: 8px;"
              >
                No data
              </div>
              <a-button
                type="primary"
                size="small"
                style="width: 90px; margin-right: 8px"
                :disabled="!selectedPmUser"
                @click="() => { applyPmUserFilter(); confirm(); }"
              >
                Search
              </a-button>
              <a-button
                size="small"
                style="width: 90px"
                @click="() => { clearPmUserFilter(); clearFilters(); }"
              >
                Reset
              </a-button>
            </template>
            <template v-else>
              <a-input
                ref="searchInput"
                :placeholder="`Search ${column.title}`"
                :value="selectedKeys[0]"
                style="width: 188px; margin-bottom: 8px; display: block"
                @change="(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])"
                @pressEnter="() => confirm()"
              />
              <a-button
                type="primary"
                size="small"
                style="width: 90px; margin-right: 8px"
                @click="() => confirm()"
              >
                <template #icon><SearchOutlined /></template>
                Search
              </a-button>
              <a-button size="small" style="width: 90px" @click="() => clearFilters()">
                Reset
              </a-button>
            </template>
          </div>
        </template>
        <template #customFilterIcon="{ filtered }">
          <SearchOutlined :style="{ color: filtered ? '#1890ff' : undefined }" />
        </template>

        <template #bodyCell="{ column, text, record }">
          <template v-if="column.dataIndex === 'id'">
            <span>#{{ text }}</span>
          </template>
          <template v-if="column.dataIndex === 'user'">
            <span>{{ formatUserName(text) }}</span>
          </template>
          <template v-if="column.dataIndex === 'status'">
            <span>{{ text || '-' }}</span>
          </template>
          <template v-if="column.dataIndex === 'pmUser'">
            <span>{{ formatUserName(text) }}</span>
          </template>
          <template v-if="column.dataIndex === 'addedAt'">
            <span>{{ formatDate(text) }}</span>
          </template>
          <template v-if="column.dataIndex === 'price'">
            <span>{{ formatPrice(text) }}</span>
          </template>
          <template v-if="column.dataIndex === 'isNewOrderChangesRequests'">
            <span :style="{ color: text ? 'orange' : 'green', fontWeight: 'bold' }">
              {{ text ? 'Yes' : 'No new changes' }}
            </span>
          </template>
          <template v-if="column.dataIndex === 'payments'">
            <span>{{ formatPayment(text) }}</span>
          </template>
          <template v-else-if="column.dataIndex === 'actions'">
            <div :class="b('action-buttons')">
              <a-button
                type="primary"
                html-type="button"
                @click="openEditWindow(record.id)"
              >
                Edit
              </a-button>
              <a-popconfirm title="Sure to delete?" @confirm="deleteOrder(record.id)">
                <a-button type="primary" html-type="button" danger>
                  Delete
                </a-button>
              </a-popconfirm>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<style src="./style.scss" scoped lang="scss"></style>
